import React from 'react'
import tw from 'twin.macro'
import { Tooltip } from '@mui/material'
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { StatusChip } from 'src/components/payments-ops/StatusChip'
import { FeeWithdrawalReportCell } from 'src/components/payments-ops/fee-withdrawals/FeeWithdrawalReportCell'
import { FeeWithdrawalStatus } from 'src/constants/payments-ops/enums'
import {
  feeWithdrawalStatusLabels,
  feeWithdrawalTypeLabels,
} from 'src/constants/payments-ops/labels'
import type { FeeWithdrawal } from 'src/interfaces/feeWithdrawal'
import { formatDateOnly, formatDateTime, parseIsoDate, parseUnixTimestamp } from 'src/utils/grid'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'
import { shortenIdentifier } from 'src/utils/paymentsOps/feeWithdrawals'

interface FeeWithdrawalColumnOptions {
  onDownloadReport(withdrawal: FeeWithdrawal): void
}

const EMPTY_VALUE = '—'

export const buildFeeWithdrawalColumns = ({
  onDownloadReport,
}: FeeWithdrawalColumnOptions): Array<GridColDef<FeeWithdrawal>> => {
  return [
    {
      field: 'shortId',
      headerName: 'Short ID',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams<FeeWithdrawal, string | undefined>) => {
        return <span css={tw`truncate font-mono text-xs`}>{params.value || EMPTY_VALUE}</span>
      },
    },
    {
      field: 'partnerName',
      headerName: 'Partner',
      flex: 1,
      minWidth: 200,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<FeeWithdrawal>) => {
        return (
          <div css={tw`flex min-w-0 flex-col`}>
            <span css={tw`truncate`}>{params.row.partnerName || EMPTY_VALUE}</span>
            <span css={tw`truncate font-mono text-xs text-shades-500`}>{params.row.partnerId}</span>
          </div>
        )
      },
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 190,
      sortable: false,
      valueGetter: (value: FeeWithdrawal['type']) => {
        return feeWithdrawalTypeLabels[value] ?? value
      },
    },
    {
      field: 'totalAmount',
      headerName: 'Amount',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      valueFormatter: (value: number) => {
        return formatCentsAsUsd(value)
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<FeeWithdrawal>) => {
        const { status, failureReasonType } = params.row
        const statusLabel = feeWithdrawalStatusLabels[status] ?? { label: status, tone: 'neutral' }
        const tooltip =
          status === FeeWithdrawalStatus.Failed && failureReasonType ? failureReasonType : ''

        return (
          <Tooltip title={tooltip}>
            <span>
              <StatusChip label={statusLabel.label} tone={statusLabel.tone} />
            </span>
          </Tooltip>
        )
      },
    },
    {
      field: 'effectiveDate',
      headerName: 'Effective date',
      width: 140,
      sortable: false,
      valueGetter: parseIsoDate,
      valueFormatter: formatDateOnly,
    },
    {
      field: 'externalTransactionId',
      headerName: 'External transaction',
      width: 190,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<FeeWithdrawal>) => {
        const { externalTransactionId, externalTransactionStatus } = params.row

        return (
          <div css={tw`flex min-w-0 flex-col`}>
            {externalTransactionId ? (
              <Tooltip title={externalTransactionId}>
                <span css={tw`truncate font-mono text-xs`}>
                  {shortenIdentifier(externalTransactionId)}
                </span>
              </Tooltip>
            ) : (
              <span>{EMPTY_VALUE}</span>
            )}
            {externalTransactionStatus ? (
              <span css={tw`truncate text-xs text-shades-500`}>{externalTransactionStatus}</span>
            ) : null}
          </div>
        )
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created at',
      width: 180,
      sortable: false,
      valueGetter: parseUnixTimestamp,
      valueFormatter: formatDateTime,
    },
    {
      field: 'updatedAt',
      headerName: 'Updated at',
      width: 180,
      sortable: false,
      valueGetter: parseUnixTimestamp,
      valueFormatter: formatDateTime,
    },
    {
      field: 'hasReport',
      headerName: 'Report',
      width: 130,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<FeeWithdrawal>) => {
        return (
          <FeeWithdrawalReportCell withdrawal={params.row} onDownloadReport={onDownloadReport} />
        )
      },
    },
  ]
}
